import { getLocale, getTranslations } from "next-intl/server"

import { HeaderContentWithScrollClient } from "./HeaderContentWithScrollClient"
import { UserInfoProviderWrapper } from "./providers/UserInfoProviderWrapper"
import { MenuItem } from "./types"
import { generateMenuData } from "./utils/generateMenuData"

import "./index.scss"

import { getAntdLocale } from "@repo/shared"
import { ConfigProvider } from "antd"

import { IHeaderCoreProps, UtilityNavItem } from "../../interface"
import { AnnouncementItem } from "../../servers/newscenter/interface"
import { NavigationItem } from "../MainNavigation/type"
import { AnnouncementBanner } from "./AnnouncementBanner"
import { TopUtilityBar } from "./components"

const TOP_UTILITY_HEIGHT = 40
const MAIN_NAV_HEIGHT = 48
const ANNOUNCEMENT_HEIGHT = 36

const buildUtilityNav = (locale: string, t: (key: string) => string): UtilityNavItem[] => {
  const getPrefix = (key: string) => {
    return `/${locale}${key}`
  }

  return [
    { label: t("utilityNav.Announcements"), href: `${getPrefix("/announcements")}`, type: "link" },
    { label: t("utilityNav.AboutUs"), href: `${getPrefix("/about-us")}`, type: "link" },
    { label: t("utilityNav.DownloadAPP"), href: `${getPrefix("/app")}`, type: "link" },
  ]
}

/**
 * 将服务端菜单数据转换为 NavigationItem 格式（服务端处理，减少客户端计算）
 */
function convertMenuItemsToNavigationItems(menu: MenuItem[]): NavigationItem[] {
  return menu.map((item) => ({
    title: item.title,
    href: item.href ?? "#",
    children: item.children?.map((child) => ({
      ...child,
      href: child.href ?? "#",
      children: child.children?.map((grandchild) => ({
        ...grandchild,
        href: grandchild.href ?? "#",
      })),
    })),
  }))
}

/**
 * 获取字体配置（服务端处理）
 */
function getFontFamily(locale: string): string {
  const fontFamilyMap: Record<string, string> = {
    th: "var(--font-noto-sans-thai), var(--font-montserrat), var(--font-roboto), Arial, Helvetica, sans-serif",
  }
  return fontFamilyMap[locale] ?? "var(--font-montserrat), var(--font-roboto), Arial, Helvetica, sans-serif"
}

/**
 * 生成样式注入（服务端处理）
 */
function generateStyleInject(
  transparentMode: boolean,
  initialHeaderMarginTopDesktop: number,
  initialHeaderMarginTopMobile: number
): React.ReactNode {
  if (transparentMode) {
    return null
  }

  return (
    <style>{`
      :root {
        --header-margin-top-desktop: ${initialHeaderMarginTopDesktop}px;
        --header-margin-top-mobile: ${initialHeaderMarginTopMobile}px;
      }
      main { margin-top: var(--header-margin-top-desktop); }
      @media (max-width: 768px) {
        main { margin-top: var(--header-margin-top-mobile); }
      }
    `}</style>
  )
}

/**
 * 注入 --announcement-height（仅服务端注入，避免抖动）。
 * 有公告时为 84px（公告条 36 + 主导航 48），无公告时为 48px；Publicity 等用该变量做 margin-top。
 */
function getAnnouncementHeightStyle(announcements: AnnouncementItem[] | undefined): React.ReactNode {
  const height = announcements?.length ? ANNOUNCEMENT_HEIGHT + MAIN_NAV_HEIGHT : MAIN_NAV_HEIGHT
  return (
    <style>{`
      :root {
        --announcement-height: ${height}px;
      }
    `}</style>
  )
}

/**
 * 服务端组件：准备菜单与导航数据，减少客户端计算
 */
export async function HeaderContentWithScroll(props: IHeaderCoreProps) {
  const locale = await getLocale()
  const t = await getTranslations("HeaderComp")

  const menu: MenuItem[] = generateMenuData(props.markets, locale, t)
  const utilityNav = buildUtilityNav(locale, t)
  const isTransparentMode = props.transparentMode !== false
  const hasAnnouncement = props.showAnnouncementBanner !== false && (props.announcements?.length ?? 0) > 0

  // 预估高度（桌面包含顶部工具栏，移动端不包含）
  const desktopHeaderMarginTop = isTransparentMode
    ? 0
    : TOP_UTILITY_HEIGHT + MAIN_NAV_HEIGHT + (hasAnnouncement ? ANNOUNCEMENT_HEIGHT : 0)
  const mobileHeaderMarginTop = isTransparentMode ? 0 : MAIN_NAV_HEIGHT + (hasAnnouncement ? ANNOUNCEMENT_HEIGHT : 0)

  // 服务端处理：菜单数据转换
  const menuItems = convertMenuItemsToNavigationItems(menu)

  // 服务端处理：Ant Design 配置
  const antdLocale = getAntdLocale(locale)
  const fontFamily = getFontFamily(locale)

  // 服务端处理：样式注入
  const styleInject = generateStyleInject(isTransparentMode, desktopHeaderMarginTop, mobileHeaderMarginTop)

  return (
    <UserInfoProviderWrapper userInfo={props.userInfo ?? null}>
      <ConfigProvider
        locale={antdLocale}
        theme={{
          token: {
            fontFamily,
            colorPrimary: "#d7000f",
            borderRadius: 2,
          },
        }}
      >
        {styleInject}
        {getAnnouncementHeightStyle(props.announcements)}
        <HeaderContentWithScrollClient
          {...props}
          menu={menu}
          menuItems={menuItems}
          initialHeaderMarginTopDesktop={desktopHeaderMarginTop}
          initialHeaderMarginTopMobile={mobileHeaderMarginTop}
        >
          <AnnouncementBanner announcements={props.announcements || []} />
          <TopUtilityBar utilityNav={utilityNav} />
        </HeaderContentWithScrollClient>
      </ConfigProvider>
    </UserInfoProviderWrapper>
  )
}
